import { parseISO, eachMonthOfInterval } from "date-fns";
import React, { FunctionComponent, useContext, useState } from "react";
import { CellValue, TableInstance } from "react-table";
import { DrugCostInfo, Plan } from "../../../@types";
import {
  AugmentedColumn,
  AugmentedHeaderProps,
  col,
} from "../../../@types/react-table";
import { AppStore } from "../../../app/store";
import { hasMailOrderPharmacy } from "../../../app/store/AppStore";
import { RenderedTable } from "../../../components";
import {
  $$,
  formatDate,
  planHasDrugCoverage,
  shouldSuppressDrugPricing,
  useTranslate,
  getCellStringValueFromHeaderProps,
  useBelowLargeBreakpoint,
  useTableConfig,
} from "../../../helpers";
import { PharmacyNetworkStatusStackedHeading } from "../components";

const EstimatedMonthlyCosts: FunctionComponent<{
  drugCostInfo: DrugCostInfo;
  plan: Plan;
}> = ({ drugCostInfo, plan }) => {
  const t = useTranslate();
  const {
    state: { language },
    state,
  } = useContext(AppStore.AppContext);
  const includesMailOrder = hasMailOrderPharmacy(state);
  const suppressDrugPricing = shouldSuppressDrugPricing(plan.redactions);
  const thisPlanHasDrugCoverage = planHasDrugCoverage(plan);
  const [isBelowLargeBreakpoint, setIsBelowLargeBreakpoint] = useState(false);

  useBelowLargeBreakpoint(matches => {
    setIsBelowLargeBreakpoint(matches);
  }, []);

  const localizedMonthNames = eachMonthOfInterval({
    start: new Date(2014, 0, 1),
    end: new Date(2014, 11, 1),
  }).map(d => formatDate(d, "MMMM", language));

  const pharmacyCols = [{ accessor: "month" }] as AugmentedColumn[];
  const monthlyTotalsByNPI = {};

  drugCostInfo.costs.forEach(pharmacy => {
    if (pharmacy.npi || includesMailOrder) {
      const totalsByMonth = {};
      pharmacy.estimated_monthly_costs.forEach(b => {
        totalsByMonth[parseISO(b.date).getMonth()] = b.total;
      });
      monthlyTotalsByNPI[pharmacy.npi || "mail-order"] = totalsByMonth;

      pharmacyCols.push({
        accessor: pharmacy.npi || "mail-order",
        Header: (props: AugmentedHeaderProps<col>) => {
          return (
            <PharmacyNetworkStatusStackedHeading
              drugCostInfo={drugCostInfo}
              includeName
              isTableHeader
              shouldPharmacyNetworkLabelsBeNarrow={true}
              npi={pharmacy.npi}
              hasDrugCoverage={thisPlanHasDrugCoverage}
              // Add costs to stacked view, sr-only content
              stackedViewScreenReaderContent={getCellStringValueFromHeaderProps(
                props
              )}
            />
          );
        },
        Cell: function cellRenderer({
          value,
        }: Partial<TableInstance> | CellValue) {
          // `aria-hide` cell value below large breakpoint. This content is in
          // the sr-visible output of the pharamacy network status component
          return <div aria-hidden={isBelowLargeBreakpoint}>{value}</div>;
        },
      });
    }
  });

  const rowData = localizedMonthNames
    .map((month, i) => {
      const row = { month };
      let isEmptyMonth = true;
      drugCostInfo.costs.forEach(pharmacy => {
        if (pharmacy.npi || includesMailOrder) {
          const npi = pharmacy.npi || "mail-order";
          const total = monthlyTotalsByNPI[pharmacy.npi || "mail-order"][i];

          if (suppressDrugPricing) {
            isEmptyMonth = false;
            row[npi] = t("na");
          } else if (total !== undefined) {
            isEmptyMonth = false;
            row[npi] = $$(total);
          } else {
            row[npi] = "-";
          }
        }
      });
      return isEmptyMonth ? { month: null } : row;
    })
    .filter(row => row.month !== null);

  const data = useTableConfig(rowData, [language, drugCostInfo]);
  const columns = useTableConfig(pharmacyCols, [
    language,
    drugCostInfo,
    isBelowLargeBreakpoint,
  ]);

  return (
    <RenderedTable
      caption={t("plan_details.drug_coverage.estimated_monthly_costs")}
      className={`pharmacy-column-table__${drugCostInfo.costs.length || 1}`}
      rowHeaders
      tableColumns={columns}
      tableData={data}
    />
  );
};

export default EstimatedMonthlyCosts;
